import { createElement } from "react";

import {
  DollarSignIcon,
  WalletIcon,
  ShoppingCartIcon,
  UsersRoundIcon,
} from "lucide-react";

import type { StatisticsCard01Props } from "./statistics-card-01";
import type { StatisticsCard03Props } from "./statistics-card-03";
import type { StatisticsCard05Props } from "./statistics-card-05";
import type { StatisticsExpenseCardProps } from "./statistics-expense-card";
import type { StatisticsImpressionCardProps } from "./statistics-impression-card";
import type { StatisticsIncomeCardProps } from "./statistics-income-card";
import type { StatisticsOrderCardProps } from "./statistics-order-card";
import type { StatisticsProfitCardProps } from "./statistics-profit-card";
import type { StatisticsTotalProfitCardProps } from "./statistics-total-profit-card";
import type { StatisticsTotalRevenueCardProps } from "./statistics-total-revenue-card";
import type { StatisticsUserReachCardProps } from "./statistics-user-reach-card";

export const statisticsCard01DemoProps: StatisticsCard01Props = {
  icon: createElement(UsersRoundIcon, { className: "size-4" }),
  value: "230k",
  title: "Sales",
  changePercentage: "+18.2%",
  comparisonLabel: "than last week",
};

export const statisticsCard03DemoProps: StatisticsCard03Props = {
  icon: createElement(ShoppingCartIcon),
  trend: "up",
  changePercentage: "+22%",
  value: "124k",
  title: "Total Orders",
  badgeContent: "Last week",
};

export const statisticsCard05DemoProps: StatisticsCard05Props = {
  icon: createElement(WalletIcon),
  title: "Total Profit",
  time: "Last week",
  value: "1.28k",
  changePercentage: 12.8,
};

export const statisticsExpenseDemoProps: StatisticsExpenseCardProps = {
  title: "Expense this month",
  amount: "$4,120",
  changePercentage: "-12.2%",
  comparisonLabel: "vs Last month",
  expenseLabel: "Expense",
  chartData: [
    { date: "2024-06-14", expense: 290 },
    { date: "2024-06-15", expense: 360 },
    { date: "2024-06-16", expense: 360 },
    { date: "2024-06-17", expense: 360 },
    { date: "2024-06-18", expense: 400 },
    { date: "2024-06-19", expense: 550 },
    { date: "2024-06-20", expense: 590 },
    { date: "2024-06-21", expense: 500 },
    { date: "2024-06-22", expense: 450 },
    { date: "2024-06-23", expense: 460 },
    { date: "2024-06-24", expense: 400 },
    { date: "2024-06-25", expense: 350 },
    { date: "2024-06-26", expense: 320 },
    { date: "2024-06-27", expense: 300 },
  ],
};

export const statisticsIncomeDemoProps: StatisticsIncomeCardProps = {
  title: "Income this month",
  amount: "$5,280",
  changePercentage: "+12.2%",
  comparisonLabel: "vs Last month",
  incomeLabel: "Income",
  chartData: [
    { date: "2024-06-24", income: 190 },
    { date: "2024-06-25", income: 285 },
    { date: "2024-06-26", income: 420 },
    { date: "2024-06-27", income: 280 },
    { date: "2024-06-28", income: 250 },
    { date: "2024-06-29", income: 500 },
    { date: "2024-06-30", income: 550 },
    { date: "2024-07-01", income: 300 },
  ],
};

export const statisticsImpressionDemoProps: StatisticsImpressionCardProps = {
  title: "Impression",
  description: "Last year",
  value: "175K",
  changePercentage: "+24%",
  impressionLabel: "Impressions",
  chartData: [
    { month: "January", impression: 20 },
    { month: "February", impression: 20 },
    { month: "March", impression: 50 },
    { month: "April", impression: 50 },
    { month: "May", impression: 30 },
    { month: "June", impression: 30 },
    { month: "July", impression: 5 },
    { month: "August", impression: 5 },
    { month: "September", impression: 50 },
    { month: "October", impression: 50 },
    { month: "November", impression: 105 },
    { month: "December", impression: 105 },
  ],
};

export const statisticsOrderDemoProps: StatisticsOrderCardProps = {
  title: "Order",
  description: "Last week",
  value: "124K",
  changePercentage: "+12.6%",
  ordersLabel: "Orders",
  chartData: [
    { day: "Monday", orders: 120 },
    { day: "Tuesday", orders: 285 },
    { day: "Wednesday", orders: 190 },
    { day: "Thursday", orders: 190 },
    { day: "Friday", orders: 315 },
    { day: "Saturday", orders: 190 },
    { day: "Sunday", orders: 220 },
  ],
};

export const statisticsProfitDemoProps: StatisticsProfitCardProps = {
  title: "Profit",
  description: "Last Month",
  value: "624K",
  changePercentage: "+12.6%",
  profitLabel: "Profit",
  chartData: [
    { month: "January", profit: 10 },
    { month: "February", profit: 75 },
    { month: "March", profit: 40 },
    { month: "April", profit: 100 },
    { month: "May", profit: 70 },
    { month: "June", profit: 110 },
  ],
};

export const statisticsTotalProfitDemoProps: StatisticsTotalProfitCardProps = {
  title: "$88.5k",
  description: "Total Profit",
  changePercentage: "-18%",
  currentLabel: "Current year",
  previousLabel: "Previous year",
  chartData: [
    { name: "Page A", previous: -2120, current: 2080 },
    { name: "Page B", previous: -1720, current: 1560 },
    { name: "Page C", previous: -2200, current: 2480 },
    { name: "Page D", previous: -1200, current: 2700 },
    { name: "Page E", previous: -2200, current: 960 },
  ],
};

export const statisticsTotalRevenueDemoProps: StatisticsTotalRevenueCardProps =
  {
    title: "$42.5k",
    description: "Total Revenue",
    changePercentage: "-22%",
    primaryLabel: "Desktop",
    secondaryLabel: "Mobile",
    chartData: [
      { month: "January", primary: 120, secondary: 80 },
      { month: "February", primary: 164, secondary: 120 },
      { month: "March", primary: 99, secondary: 138 },
      { month: "April", primary: 48, secondary: 80 },
      { month: "May", primary: 160, secondary: 141 },
    ],
  };

export const statisticsUserReachDemoProps: StatisticsUserReachCardProps = {
  title: "User reach",
  description: "Last week",
  value: "32K",
  changePercentage: "+12%",
  visitors: 500,
  visitorsLabel: "Visitors",
};

export const statisticsIconDollar = createElement(DollarSignIcon, {
  className: "size-4",
});
